import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import './LinkedInAuthMobile.css';

const CONFIG = {
  APP_DEEP_LINK: 'gigfws://linkedin-auth',
  WEB_FALLBACK_URL: process.env.REACT_APP_URL || window.location.origin,
  COUNTDOWN_SECONDS: 5,
  MOBILE_REDIRECT_DELAY: 2000,
  WEB_REDIRECT_DELAY: 1000
};

const LinkedInAuthStatic = () => {
  const [currentState, setCurrentState] = useState('loading');
  const [countdown, setCountdown] = useState(CONFIG.COUNTDOWN_SECONDS);
  const [errorText, setErrorText] = useState('Unknown error occurred');
  const [deviceInfo, setDeviceInfo] = useState({ deviceType: 'Unknown', platform: 'Unknown' });
  const location = useLocation();
  const history = useHistory();
  const countdownIntervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const isAndroid = () => {
    return /Android/.test(navigator.userAgent);
  };

  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    let deviceType = 'Desktop';
    let platform = 'Web';

    if (isIOS()) {
      deviceType = 'iOS';
      platform = 'iOS';
    } else if (isAndroid()) {
      deviceType = 'Android';
      platform = 'Android';
    } else if (isMobile()) {
      deviceType = 'Mobile';
      platform = 'Mobile Web';
    }

    return { deviceType, platform };
  };

  const showState = (state) => {
    setCurrentState(state);
  };

  const startCountdown = (seconds, callback) => {
    let timeLeft = seconds;
    setCountdown(timeLeft);

    countdownIntervalRef.current = setInterval(() => {
      timeLeft--;
      setCountdown(timeLeft);

      if (timeLeft <= 0) {
        clearInterval(countdownIntervalRef.current);
        if (callback) callback();
      }
    }, 1000);
  };

  const redirectToApp = (code, state, error) => {
    let redirectUrl = CONFIG.APP_DEEP_LINK;
    const params = new URLSearchParams();

    if (code) {
      params.append('code', code);
    }
    if (state) {
      params.append('state', state);
    }
    if (error) {
      params.append('error', error);
    }

    if (params.toString()) {
      redirectUrl += '?' + params.toString();
    }

    window.location.href = redirectUrl;

    redirectTimeoutRef.current = setTimeout(() => {
      showState('mobile-fallback');
    }, CONFIG.MOBILE_REDIRECT_DELAY);
  };

  const redirectToWeb = (code, state, error) => {
    let redirectUrl = CONFIG.WEB_FALLBACK_URL;
    const params = new URLSearchParams();

    if (code) {
      params.append('code', code);
    }
    if (state) {
      params.append('state', state);
    }
    if (error) {
      params.append('error', error);
    }

    if (params.toString()) {
      redirectUrl += '/linkedin-web-auth?' + params.toString();
    }

    window.location.href = redirectUrl;
  };

  const showMobileFallback = () => {
    const info = getDeviceInfo();
    setDeviceInfo(info);
    showState('mobile-fallback');
  };

  const downloadApp = () => {
    if (isIOS()) {
      window.open('https://apps.apple.com/app/gigfws', '_blank');
    } else if (isAndroid()) {
      window.open('https://play.google.com/store/apps/details?id=com.gigfws.app', '_blank');
    } else {
      window.open('https://gigfws.com/download', '_blank');
    }
  };

  const goBack = () => {
    history.goBack();
  };

  const retryAuthentication = () => {
    const currentUrl = new URL(window.location);
    const clientId = currentUrl.searchParams.get('client_id') || 'your_client_id';
    const redirectUri = currentUrl.searchParams.get('redirect_uri') || window.location.origin + window.location.pathname;
    const scope = currentUrl.searchParams.get('scope') || 'r_liteprofile r_emailaddress';

    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `state=${Math.random().toString(36).substring(7)}`;

    window.location.href = authUrl;
  };

  const processAuthentication = () => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const state = urlParams.get('state');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      setErrorText(errorDescription || error);
      showState('error');
      return;
    }

    if (!code) {
      setErrorText('No authorization code received from LinkedIn');
      showState('error');
      return;
    }

    showState('success');
    startCountdown(CONFIG.COUNTDOWN_SECONDS, () => {
      if (isMobile()) {
        redirectToApp(code, state);
      } else {
        redirectToWeb(code, state);
      }
    });
  };

  useEffect(() => {
    const deviceInfoData = getDeviceInfo();
    setDeviceInfo(deviceInfoData);

    const timer = setTimeout(() => {
      processAuthentication();
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && currentState === 'success') {
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentState]);

  return (
    <div className="linkedin-auth-static-container">
      <div className="container fade-in">
        <div className="logo">
          <svg viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </div>

        {currentState === 'loading' && (
          <div id="loading-state">
            <div className="status-icon loading">
              <div className="spinner"></div>
            </div>
            <h1>Authenticating with LinkedIn</h1>
            <p>Please wait while we process your authentication...</p>
            <div className="countdown" id="countdown">
              Redirecting in <span id="timer">{countdown}</span> seconds
            </div>
          </div>
        )}

        {currentState === 'success' && (
          <div id="success-state">
            <div className="status-icon success">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h2>Authentication Successful!</h2>
            <p>You have successfully authenticated with LinkedIn. Redirecting to the app...</p>
            <div className="message success">
              <strong>Success!</strong> Your LinkedIn account has been connected successfully.
            </div>
          </div>
        )}

        {currentState === 'error' && (
          <div id="error-state">
            <div className="status-icon error">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </div>
            <h2>Authentication Failed</h2>
            <p>We encountered an issue with your LinkedIn authentication.</p>
            <div className="message error" id="error-message">
              <strong>Error:</strong> <span id="error-text">{errorText}</span>
            </div>
            <div className="button" onClick={retryAuthentication}>Try Again</div>
            <div className="button secondary" onClick={goBack}>Go Back</div>
          </div>
        )}

        {currentState === 'mobile-fallback' && (
          <div id="mobile-fallback">
            <h2>Mobile App Required</h2>
            <p>This authentication requires the GIGFWS mobile app to be installed on your device.</p>
            <div className="device-info">
              <strong>Device:</strong> <span id="device-type">{deviceInfo.deviceType}</span><br />
              <strong>Platform:</strong> <span id="platform">{deviceInfo.platform}</span>
            </div>
            <div className="button" onClick={downloadApp}>Download App</div>
            <div className="button secondary" onClick={goBack}>Go Back</div>
          </div>
        )}

        <div className="footer">
          <p>&copy; 2024 GIGFWS. All rights reserved.</p>
          <p>Powered by LinkedIn OAuth 2.0</p>
        </div>
      </div>
    </div>
  );
};

export default LinkedInAuthStatic;

